import { useEffect } from 'react';

import { accountPageSlugs } from '@boss/constants/b2b-b2c';
import { useRouter } from '@boss/hooks';

import { useAccountCampaignInfo } from '../../client-queries';
import { isB2b } from '../../utils';
import { useCampaignEligibility } from '../use-campaign-eligibility/use-campaign-eligibility';
import { useProfile } from '../use-profile/use-profile';

export type DeepLinkEligibilityGuard = { pending: boolean; blocked: boolean };

type DeepLinkEligibilityInput = {
  campaignId: string;
  categories?: string[];
};

/**
 * [45176] Blocked deep-link redirect behavior for /actie/<campaignId>[?categories=…].
 *
 * Direct navigation to an ineligible campaign/category deep-link redirects
 * immediately: anonymous → homepage, logged-in → account page. There are
 * exactly two targets — never an intermediate parent (AC-17). Mirrors
 * usePermissionGuard (use-route-guard): settle the profile first, wait while
 * eligibility is loading, then fail closed when it cannot be verified.
 */
export const useDeepLinkEligibilityGuard = ({
  campaignId,
  categories = [],
}: DeepLinkEligibilityInput): DeepLinkEligibilityGuard => {
  const { isLoggedIn, status } = useProfile();
  const { replace, locale } = useRouter();
  const { isEligibilityLoaded, isCampaignEligible, isCategoryTileEligible } = useCampaignEligibility(locale);
  // Same react-query cache entry as useCampaignEligibility's — no extra network
  // request; read only to tell a failed fetch (fail closed) from one in flight.
  const { isLoading: eligibilityLoading, isError: eligibilityError } = useAccountCampaignInfo(locale);

  // [45176] B2B-only: guard stays inert on the colora (B2C) build and for
  // malformed URLs without a campaignId (existing page behavior handles those).
  const gated = isB2b && !!campaignId;

  const ineligible =
    !isCampaignEligible(campaignId) || (categories.length > 0 && !isCategoryTileEligible(campaignId, categories));

  // [45176] Fail closed: an errored eligibility fetch (or a loaded, ineligible
  // path) blocks; a loading fetch is pending, not blocked — wait, don't bounce.
  const pending = gated && (status !== 'idle' || (eligibilityLoading && !eligibilityError));
  const blocked = gated && status === 'idle' && (eligibilityError || (isEligibilityLoaded && ineligible));

  useEffect(() => {
    if (!gated) {
      return;
    }
    // Only redirect once the profile is definitively resolved — wait, don't bounce.
    if (status !== 'idle') {
      return;
    }
    // Eligibility still in flight — wait, don't bounce (same patience as
    // usePermissionGuard's permissionsLoading).
    if (eligibilityLoading && !eligibilityError) {
      return;
    }
    if (blocked) {
      replace(isLoggedIn ? `/${locale}/${accountPageSlugs[locale]}` : `/${locale}`);
    }
  }, [gated, status, eligibilityLoading, eligibilityError, blocked, isLoggedIn, locale, replace]);

  return { pending, blocked };
};
